/**
 * Unit models, batch 1 (ADR-0075): civilians and the ancient and classical land army,
 * with the unique units that replace them.
 */

import * as T from 'three';
import { COL, horse, kit, mat, ox, person, shadeOf, type HorseOptions, type Kit, type PersonOptions, type V3 } from './parts.js';
import type { ModelBuilder } from './units.js';

const DARK_SKIN = 0x7a4e32;
const BROWN_SKIN = 0xb07a52;

/** A rider on a horse: the horse's kit and the rider's (in the rider's own frame). */
export function mounted(g: T.Group, civ: number, h: HorseOptions, p: PersonOptions): { horse: Kit; rider: Kit; frame: T.Group } {
  const hk = horse(g, civ, h);
  const frame = new T.Group();
  frame.position.set(-0.4, 4.3, 0);
  g.add(frame);
  const rk = person(frame, civ, { seated: true, skirt: false, ...p });
  return { horse: hk, rider: rk, frame };
}

/** A spoked two-wheeled car with a front rail, painted in the owner's colour. */
function chariotCar(k: Kit, civ: number, x: number, heavy: boolean): void {
  k.box(4.2, 0.5, 4.6, x, 4.0, 0, COL.WOODD);
  k.shape([[0, 0], [4.2, 0], [4.2, 3.0], [3.0, 3.6], [0, 1.8]], 0.25, civ, x - 2.1, 4.25, 2.1);
  k.shape([[0, 0], [4.2, 0], [4.2, 3.0], [3.0, 3.6], [0, 1.8]], 0.25, civ, x - 2.1, 4.25, -2.35);
  k.box(0.25, 3.0, 4.6, x + 2.0, 5.8, 0, shadeOf(civ, 0.85));
  k.bar([x + 2.0, 7.3, -2.3], [x + 2.0, 7.3, 2.3], 0.12, 0.12, COL.BRONZE, 5);
  for (const z of [2.75, -2.75]) k.wheel(x, 3.2, z, 3.0, COL.WOODD, heavy ? 8 : 6, 0.32);
  k.bar([x, 3.2, -3.1], [x, 3.2, 3.1], 0.2, 0.2, COL.WOODD, 6);
  k.bar([x + 2.0, 4.4, 0], [x + 9.5, 9.2, 0], 0.18, 0.18, COL.WOODD, 6);
}

export const ANCIENT: Record<string, ModelBuilder> = {
  warrior(g, civ) {
    const k = person(g, civ, {
      helmet: 'hair',
      tunic: 0xb08a5c,
      skirtColour: 0x9c7648,
      legs: COL.SKIN,
      sleeve: COL.SKIN,
      lHand: [2.0, 9.0, 2.0],
      rHand: [0.6, 15.6, -1.9],
      rElbow: [-0.6, 13.6, -2.4],
    });
    // A sash and arm band in the owner's colour on the hide tunic.
    k.box(0.15, 4.8, 0.7, 1.2, 9.3, 0, civ, 0.5);
    k.ball(0.55, 0, 11.4, 1.5, civ, 0);
    // A club raised overhead, with a bound stone head.
    k.bar([0.6, 14.4, -1.9], [1.8, 19.6, -1.6], 0.22, 0.3, COL.WOOD, 6);
    k.ball(0.95, 1.9, 20.0, -1.6, 0x8f8a80, 0);
    k.bar([1.5, 19.2, -1.6], [2.2, 19.2, -1.6], 0.35, 0.35, COL.LEATHER, 6);
    // A small hide shield.
    k.disc(2.0, 9, 0xc4a77a, 2.2, 9.0, 2.35, -0.35);
    k.disc(1.2, 9, civ, 2.2, 9.0, 2.42, -0.35);
  },

  scout(g, civ) {
    const k = person(g, civ, {
      helmet: 'hood',
      cape: shadeOf(civ, 0.7),
      legs: 0x8a7a5c,
      lHand: [1.6, 13.9, 1.0],
      lElbow: [1.4, 11.6, 2.2],
      rHand: [1.6, 8.0, -1.9],
    });
    k.bar([1.6, 0, -1.9], [1.6, 15.5, -1.9], 0.16, 0.18, COL.WOOD, 6);
    k.ball(0.32, 1.6, 15.6, -1.9, COL.WOODD, 0);
    k.box(1.1, 1.2, 0.6, -0.4, 6.9, 1.5, COL.LEATHER);
    k.bar([-1.3, 9.0, -1.0], [-1.6, 6.0, -1.1], 0.5, 0.5, COL.LEATHER, 8);
  },

  worker(g, civ) {
    const k = person(g, civ, {
      helmet: 'cap',
      legs: 0x8a7a5c,
      lHand: [1.4, 12.6, 1.2],
      rHand: [-0.2, 10.4, -1.6],
    });
    // A pick over the shoulder and a basket at the feet.
    k.bar([-1.6, 8.8, -1.8], [2.6, 14.4, 1.4], 0.14, 0.16, COL.WOOD, 6);
    k.bar([2.6, 14.4, 1.4], [1.0, 16.6, 1.0], 0.14, 0.06, COL.IRON, 5);
    k.bar([2.6, 14.4, 1.4], [4.2, 13.0, 1.6], 0.14, 0.06, COL.IRON, 5);
    k.bar([2.6, 0.2, -2.6], [2.6, 2.2, -2.6], 1.1, 0.9, 0xb8975e, 10);
    k.bar([2.6, 2.2, -2.6], [2.6, 2.5, -2.6], 1.15, 1.15, COL.WOODD, 10);
    for (const x of [2.1, 2.9]) k.ball(0.45, x, 2.6, -2.6, 0x8f8a80, 0);
    k.box(0.12, 4.4, 0.4, 1.28, 9.6, 0, COL.LEATHER, 0.5);
  },

  settler(g, civ) {
    // A covered cart drawn by an ox, with a settler walking beside it.
    const team = new T.Group();
    team.position.set(7.5, 0, 0);
    team.scale.setScalar(0.85);
    g.add(team);
    ox(team, civ);
    const k = kit(g);
    k.box(8.0, 0.6, 5.0, -2.0, 4.0, 0, COL.WOODD);
    for (const z of [2.4, -2.4]) k.box(8.0, 1.5, 0.25, -2.0, 5.0, z, COL.WOOD);
    const cover = new T.Mesh(new T.CylinderGeometry(2.6, 2.6, 8.2, 10, 1, true, -Math.PI / 2, Math.PI), mat(COL.WHITE, true));
    cover.rotation.z = Math.PI / 2;
    cover.position.set(-2.0, 5.6, 0);
    g.add(cover);
    for (const x of [-5.6, -2.0, 1.6]) {
      const hoop = new T.Mesh(new T.TorusGeometry(2.65, 0.1, 4, 10, Math.PI), mat(COL.WOODD));
      hoop.rotation.y = Math.PI / 2;
      hoop.position.set(x, 5.6, 0);
      g.add(hoop);
    }
    for (const z of [2.66, -2.66]) k.box(8.0, 0.6, 0.05, -2.0, 6.6, z * 0.92, civ);
    for (const x of [-4.6, 0.6]) for (const z of [2.9, -2.9]) k.wheel(x, 2.2, z, 2.1, COL.WOODD, 8);
    k.bar([2.0, 4.0, 0], [8.4, 10.6, 0], 0.16, 0.16, COL.WOODD, 5);
    for (const [x, y] of [[-5.0, 7.0], [-4.0, 7.2], [-4.5, 7.8]] as Array<[number, number]>) k.box(1.0, 0.8, 1.0, x, y, 0, 0xc4a77a);
    const walker = new T.Group();
    walker.position.set(3.0, 0, 5.6);
    walker.scale.setScalar(0.8);
    g.add(walker);
    const w = person(walker, civ, { helmet: 'cap', legs: 0x8a7a5c, rHand: [1.6, 8.0, -1.9] });
    w.bar([1.6, 0, -1.9], [1.6, 14.5, -1.9], 0.16, 0.18, COL.WOOD, 6);
  },

  horseman(g, civ) {
    const { rider: k } = mounted(g, civ, { saddleCloth: true }, {
      helmet: 'cap',
      legs: 0x8a7a5c,
      boots: COL.LEATHER,
      lHand: [2.2, 8.4, 1.0],
      rHand: [0.4, 13.8, -1.8],
      rElbow: [-0.6, 12.2, -2.4],
    });
    // A javelin raised to throw, two more in a case behind the leg.
    k.bar([-3.2, 12.8, -1.9], [6.8, 15.6, -1.7], 0.11, 0.11, COL.WOOD, 5);
    k.cone([6.8, 15.6, -1.7], [7.9, 15.9, -1.68], 0.2, COL.BRONZE, 5);
    for (const dz of [0, 0.4]) k.bar([-2.2, 5.0, 2.6 + dz], [-0.6, 10.6, 2.6 + dz], 0.08, 0.08, COL.WOOD, 4);
    k.bar([-1.9, 5.4, 2.8], [-1.1, 8.0, 2.8], 0.45, 0.45, COL.LEATHER, 6);
  },

  chariot(g, civ) {
    const k = kit(g);
    chariotCar(k, civ, -4.0, false);
    const pair = new T.Group();
    pair.position.set(5.0, 0, 0);
    pair.scale.setScalar(0.82);
    g.add(pair);
    for (const z of [1.6, -1.6]) {
      const one = new T.Group();
      one.position.z = z;
      pair.add(one);
      horse(one, civ, { bare: true });
    }
    const crew = new T.Group();
    crew.position.set(-4.2, 4.3, 0);
    crew.scale.setScalar(0.92);
    g.add(crew);
    const d = person(crew, civ, { helmet: 'bronze', skirt: false, legs: COL.SKIN, lHand: [2.6, 9.0, 0.9], rHand: [1.0, 12.6, -1.6], rElbow: [-0.4, 11.0, -2.3] });
    d.bar([-3.0, 11.2, -1.8], [6.8, 14.6, -1.6], 0.12, 0.12, COL.WOOD, 5);
    d.cone([6.8, 14.6, -1.6], [8.0, 15.0, -1.6], 0.24, COL.BRONZE, 5);
  },

  elamite_chariot(g, civ) {
    const k = kit(g);
    chariotCar(k, civ, -4.4, true);
    // A parasol over the car and a quiver on its side: Elam's heavy chariot.
    k.bar([-5.8, 4.3, 0], [-5.8, 17.6, 0], 0.1, 0.1, COL.WOODD, 5);
    const shade = new T.Mesh(new T.ConeGeometry(3.4, 1.4, 10), [mat(civ), mat(shadeOf(civ, 0.8))]);
    shade.position.set(-5.8, 17.8, 0);
    g.add(shade);
    k.bar([-2.4, 4.6, 2.5], [-1.6, 8.6, 2.5], 0.5, 0.55, COL.LEATHER, 8);
    const pair = new T.Group();
    pair.position.set(4.8, 0, 0);
    pair.scale.setScalar(0.82);
    g.add(pair);
    for (const z of [1.6, -1.6]) {
      const one = new T.Group();
      one.position.z = z;
      pair.add(one);
      horse(one, civ, { bare: true, coat: 0xd9c7a8 });
    }
    for (const [x, z, pose] of [[-3.2, 0.9, 'driver'], [-5.0, -1.0, 'archer']] as Array<[number, number, string]>) {
      const c = new T.Group();
      c.position.set(x, 4.3, z);
      c.scale.setScalar(0.88);
      g.add(c);
      if (pose === 'driver') person(c, civ, { helmet: 'cap', skirt: false, legs: COL.SKIN, lHand: [2.4, 9.2, 1.0], rHand: [2.4, 9.2, -1.0] });
      else {
        const a = person(c, civ, { helmet: 'bronze', skirt: false, legs: COL.SKIN, lHand: [3.0, 11.6, 0.4], rHand: [-0.4, 11.6, -1.2] });
        a.bar([3.4, 7.0, 0.4], [3.6, 16.2, 0.4], 0.12, 0.12, COL.WOODD, 5);
        a.bar([3.4, 7.0, 0.4], [-0.4, 11.6, -1.0], 0.03, 0.03, COL.WHITE, 3);
        a.bar([-0.4, 11.6, -1.0], [3.6, 16.2, 0.4], 0.03, 0.03, COL.WHITE, 3);
      }
    }
  },

  swordsman(g, civ) {
    const k = person(g, civ, {
      helmet: 'bronze',
      cuirass: true,
      greaves: true,
      legs: COL.SKIN,
      sleeve: COL.SKIN,
      lHand: [2.0, 9.4, 2.0],
      rHand: [1.6, 14.6, -1.8],
      rElbow: [0.4, 12.6, -2.6],
    });
    // A short sword raised, and an oval shield in the owner's colour.
    k.box(0.35, 4.4, 0.12, 2.0, 17.0, -1.8, COL.STEEL, 0, 0, -0.25);
    k.box(1.4, 0.3, 0.3, 1.65, 14.9, -1.8, COL.BRONZE, 0, 0, -0.25);
    const shield = new T.Mesh(new T.CylinderGeometry(2.6, 2.6, 0.4, 14), [mat(COL.BRONZE), mat(civ), mat(COL.BRONZE)]);
    shield.rotation.x = Math.PI / 2;
    shield.scale.set(0.75, 1, 1.3);
    shield.position.set(2.2, 8.8, 2.5);
    shield.rotation.y = -0.3;
    g.add(shield);
    k.box(0.12, 5.4, 0.6, 2.35, 8.8, 2.75, COL.WHITE, 0, -0.3);
  },

  legion(g, civ) {
    const k = person(g, civ, {
      helmet: 'galea',
      legs: COL.SKIN,
      sleeve: civ,
      boots: COL.LEATHER,
      lHand: [2.2, 8.8, 2.0],
      rHand: [1.0, 13.0, -1.8],
      rElbow: [-0.2, 11.0, -2.4],
    });
    // Banded armour over the tunic.
    for (const y of [8.4, 9.2, 10.0, 10.8]) k.bar([0, y, 0], [0, y + 0.55, 0], 1.62, 1.62, COL.STEEL, 10).scale.z = 0.82;
    k.box(1.8, 0.7, 3.4, 0, 11.6, 0, COL.STEEL);
    // A tall curved shield with a boss and an emblem, and a raised throwing spear.
    const scutum = new T.Mesh(new T.CylinderGeometry(4.0, 4.0, 8.0, 10, 1, true, -0.55, 1.1), mat(civ, true));
    scutum.position.set(-1.6, 8.2, 2.3);
    scutum.rotation.y = -0.3 + Math.PI / 2;
    g.add(scutum);
    k.ball(0.55, 2.35, 8.3, 2.7, COL.BRONZE, 1);
    k.box(0.1, 0.4, 3.0, 2.38, 10.8, 2.35, COL.BRONZE, 0, -0.3);
    k.box(0.1, 0.4, 3.0, 2.38, 5.8, 2.35, COL.BRONZE, 0, -0.3);
    k.bar([-2.6, 11.4, -1.9], [7.4, 15.4, -1.6], 0.12, 0.12, COL.WOOD, 5);
    k.cone([7.4, 15.4, -1.6], [9.4, 16.2, -1.55], 0.12, COL.IRON, 4);
  },

  taseti_archer(g, civ) {
    const k = person(g, civ, {
      helmet: 'feather',
      skin: DARK_SKIN,
      tunic: DARK_SKIN,
      skirtColour: COL.WHITE,
      legs: DARK_SKIN,
      sleeve: DARK_SKIN,
      boots: 0x9a7a52,
      lHand: [3.4, 11.4, 1.0],
      lElbow: [1.7, 11.6, 1.8],
      rHand: [-0.3, 11.4, -0.4],
      rElbow: [-1.6, 11.0, -1.6],
    });
    // Sash and arm bands in the owner's colour.
    k.box(0.15, 5.0, 0.7, 1.22, 9.4, 0, civ, 0.5);
    k.bar([0, 7.6, 0], [0, 8.2, 0], 1.42, 1.42, civ, 10).scale.z = 0.8;
    // The great Nubian bow, taller than the archer.
    const points: V3[] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12 - 0.5;
      points.push([3.6 - 1.4 * t * t * 4 * 0.4, 11.4 + t * 15.0, 1.0]);
    }
    for (let i = 0; i < 12; i++) k.bar(points[i]!, points[i + 1]!, 0.17, 0.17, COL.WOODD, 5);
    k.bar(points[0]!, [-0.3, 11.4, -0.2], 0.035, 0.035, COL.WHITE, 3);
    k.bar(points[12]!, [-0.3, 11.4, -0.2], 0.035, 0.035, COL.WHITE, 3);
    k.bar([-0.3, 11.4, -0.2], [5.2, 11.4, 0.9], 0.07, 0.07, COL.WOOD, 4);
    k.bar([-1.3, 8.6, -0.6], [-1.6, 13.2, 0.3], 0.55, 0.6, 0xa7865a, 8);
    for (const dz of [-0.2, 0.2]) k.box(0.08, 0.9, 0.3, -1.7, 13.8, 0.3 + dz, COL.WHITE);
  },

  andean_slinger(g, civ) {
    const k = person(g, civ, {
      helmet: 'woolcap',
      skin: BROWN_SKIN,
      legs: BROWN_SKIN,
      sleeve: BROWN_SKIN,
      skirt: true,
      lHand: [2.2, 9.6, 1.6],
      rHand: [0.2, 16.4, -1.4],
      rElbow: [-0.4, 14.0, -2.2],
    });
    // A poncho over the tunic with woven stripes.
    k.bar([0, 8.0, 0], [0, 11.8, 0], 2.4, 1.6, shadeOf(civ, 0.95), 4).rotation.y = Math.PI / 4;
    for (const y of [8.6, 9.6]) k.bar([0, y, 0], [0, y + 0.35, 0], 2.3 - (y - 8.6) * 0.18, 2.3 - (y - 8.6) * 0.18, COL.WHITE, 4).rotation.y = Math.PI / 4;
    // The sling whirling overhead: a cord loop and a stone, and a pouch of stones.
    const loop = new T.Mesh(new T.TorusGeometry(2.4, 0.06, 4, 18), mat(COL.ROPE));
    loop.position.set(0.2, 18.6, -1.4);
    loop.rotation.x = Math.PI / 2.4;
    g.add(loop);
    k.ball(0.4, 2.6, 18.9, -1.0, 0x8f8a80, 0);
    k.bar([0.2, 16.4, -1.4], [0.2, 18.6, -1.4], 0.05, 0.05, COL.ROPE, 3);
    k.box(1.0, 1.0, 0.6, -0.6, 7.0, 1.5, 0xb8975e);
  },

  hwarang(g, civ) {
    const k = person(g, civ, {
      helmet: 'topknot',
      legs: COL.WHITE,
      boots: COL.BLACK,
      lHand: [1.8, 9.2, 1.4],
      rHand: [2.2, 13.4, -1.6],
      rElbow: [0.6, 12.0, -2.4],
    });
    // Lamellar plates over the chest and a long straight sword held high.
    for (let y = 8.6; y <= 11.0; y += 0.6)
      for (const z of [-0.8, -0.27, 0.27, 0.8]) k.box(0.1, 0.5, 0.48, 1.32, y, z, 0xb8b0a0);
    k.box(0.28, 7.4, 0.12, 3.6, 17.4, -1.6, COL.STEEL, 0, 0, -0.3);
    k.box(0.9, 0.25, 0.9, 2.5, 13.8, -1.6, COL.BRONZE, 0, 0, -0.3);
    k.bar([2.2, 13.4, -1.6], [1.8, 12.4, -1.6], 0.16, 0.16, COL.BLACK, 6);
    // A pennant from the back in the owner's colour.
    k.bar([-1.4, 7.4, 0], [-1.6, 18.0, 0], 0.08, 0.08, COL.WOODD, 4);
    k.shape([[0, 0], [0, -4.0], [-2.4, -3.4], [-2.4, -0.6]], 0, civ, -1.6, 17.8, 0, Math.PI / 2);
  },
};
